import CartService from '@/services/isomorphic/CartService';
import LoggerService from '@/services/isomorphic/LoggerService';
import CartModel from '@/services/models/Cart/CartModel';
import type ICart from '@/services/models/Cart/ICart';
import { MoneyModel } from '@/services/models/Money';
import type { Nullable } from '@/type-utils';
import MemoryCache from '@/utils/MemoryCache';

/**
 * **Display** shipping for one applicable method: merge `sourceCart` into an
 * ad-hoc cart, revalidate, select the method, then read `shippingCost`.
 *
 * Revalidate is expensive but required: applicable methods depend on cart and address (e.g. PO
 * box / US territory rules).
 *
 * `previewCache` stores the preview cart DTO per `shippingMethodUid` so repeat previews reuse
 * one ad-hoc cart per method.
 *
 * @param sourceCart - Scenario (lines, address, promotions).
 * @param shippingMethodUid - Method `uid`; must still be applicable after revalidate or this
 *   returns `null`.
 * @param previewCache - Session-scoped cache (e.g. CartVM temp cache, Apple Pay session cache).
 * @returns Resolved shipping money after selection, or `null` if the method is not applicable.
 */
export async function previewShippingCostForMethod(
  sourceCart: CartModel,
  shippingMethodUid: string,
  previewCache: MemoryCache<ICart>
): Promise<Nullable<MoneyModel>> {
  let existingCart: CartModel | undefined;

  if (previewCache.has(shippingMethodUid)) {
    try {
      existingCart = CartModel.from(previewCache.get(shippingMethodUid));
      existingCart.update({ ...sourceCart.toDTO(), uuid: existingCart.uuid });
    } catch (error) {
      LoggerService.error(
        new Error('Shipping method preview: failed to reuse cached cart.', {
          cause: error
        })
      );
      previewCache.remove(shippingMethodUid);
    }
  }

  const copyCart =
    existingCart ??
    // We must use an ad-hoc cart instead of just cloning the source cart to
    // ensure changes on the clone aren't written back to source cart in AWS.
    (await CartService.getNewAdHocCartFromSource(sourceCart.toDTO()));

  await copyCart.revalidate();

  const applicableShippingMethods = await copyCart.applicableShippingMethods;
  const selectedMethod = applicableShippingMethods.find(
    (method) => method.uid === shippingMethodUid
  );

  if (!selectedMethod) {
    return null;
  }

  await copyCart.selectShippingMethod(shippingMethodUid);

  const resolvedShipping = await copyCart.shippingCost;

  previewCache.add(shippingMethodUid, copyCart.toDTO());

  return resolvedShipping;
}
